/**
 * ч.1093: «Оператор первинки» (до ч.1099 — «Вхідні документи») — полка файлів постачальника на розпізнавання
 * (Oblik+/AI): рахунок / видаткова накладна / акт → після перевірки —
 * чернетка прибуткової накладної або рахунку постачальника.
 *
 * Розпізнавання іде у фоні (воркер backend): список сам оновлюється, поки є
 * файли «у черзі»/«розпізнається». Кредити Oblik+/AI списуються за фактом
 * токенів, як у помічнику виписки.
 */
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ai } from '@/api/ai';
import { aiAvailableText } from '@/shared/utils/aiCredits';
import {
  KIND_LABELS, STATUS_LABELS, recognition,
  type IncomingDocumentListItem, type IncomingStatus,
} from '@/api/recognition';
import { useAllPermissionsByOrg, usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { extractApiError } from '@/utils/extractApiError';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import AiBadge from '@/shared/components/AiBadge';
import DocStatusBadge, { type BadgeTone } from '@/shared/components/DocStatusBadge';

export const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.xlsx,.docx,application/pdf,image/*';
const STATUS_TONE: Record<IncomingStatus, BadgeTone> = {
  QUEUED: 'gray', RECOGNIZING: 'blue', RECOGNIZED: 'amber', CONFIRMED: 'green',
  REJECTED: 'gray', FAILED: 'red', SPLIT: 'gray',
};
const ACTIVE = new Set<IncomingStatus>(['QUEUED', 'RECOGNIZING']);
const fmt2 = (v: string | number | null) =>
  v === null || v === '' ? '' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const muted = { color: 'var(--text-secondary)', fontSize: '0.85rem' } as const;

export default function IncomingDocumentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { availableOrgs, hasPermissionInAnyOrg, loading } = usePermissionsByOrg('recognition.view');
  const { permissionsByOrg } = useAllPermissionsByOrg();
  const [orgId, setOrgId] = useState('');
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  if (!orgId && availableOrgs[0]) setOrgId(availableOrgs[0].id);
  const canUpload = !!orgId && (permissionsByOrg[orgId] ?? []).includes('recognition.upload');

  const list = useQuery({
    queryKey: ['incoming-documents', orgId, filter],
    queryFn: () => recognition.list({ organization_id: orgId, status: filter === 'open' ? 'open' : undefined, limit: 300 }),
    enabled: !!orgId,
    refetchInterval: (q) => (q.state.data?.items.some((r) => ACTIVE.has(r.status)) ? 5000 : false),
  });
  const balance = useQuery({ queryKey: ['ai-balance'], queryFn: ai.balance, staleTime: 60_000 });

  const upload = useMutation({
    mutationFn: (files: File[]) => recognition.upload(orgId, files),
    onSuccess: (rows) => {
      toast.show({ type: 'success', message: `Прийнято файлів: ${rows.length}. Розпізнавання триває — результат зʼявиться у списку.` });
      qc.invalidateQueries({ queryKey: ['incoming-documents'] });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося завантажити файли') }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => recognition.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incoming-documents'] }),
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося видалити') }),
  });

  function pickFiles(fl: FileList | null) {
    const files = Array.from(fl ?? []);
    if (!files.length) return;
    if (!canUpload) { toast.show({ type: 'warning', message: 'У цій організації немає права завантажувати документи на розпізнавання' }); return; }
    upload.mutate(files);
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault(); setDragOver(false); pickFiles(e.dataTransfer.files);
  }

  // Після завантаження — одразу відкрити картку, якщо файл один.
  useEffect(() => {
    const only = upload.data?.length === 1 ? upload.data[0] : undefined;
    if (only) navigate(`/incoming-documents/${only.id}`);
  }, [upload.data, navigate]);

  const cols = useMemo<ColumnDef<IncomingDocumentListItem>[]>(() => [
    { key: 'filename', header: 'Файл', cell: (r) => r.filename, sortValue: (r) => r.filename },
    { key: 'doc_kind', header: 'Тип', width: 150,
      cell: (r) => (r.doc_kind ? KIND_LABELS[r.doc_kind] : ''), sortValue: (r) => r.doc_kind ?? '' },
    { key: 'supplier_name', header: 'Постачальник', cell: (r) => r.supplier_name ?? '', sortValue: (r) => r.supplier_name ?? '' },
    { key: 'doc_number', header: '№ / дата', width: 170,
      cell: (r) => [r.doc_number, r.doc_date ? new Date(r.doc_date).toLocaleDateString('uk-UA') : null].filter(Boolean).join(' від '),
      sortValue: (r) => r.doc_date ?? '' },
    { key: 'total', header: 'Разом з ПДВ', width: 130, align: 'right', cell: (r) => fmt2(r.total), sortValue: (r) => Number(r.total ?? 0) },
    { key: 'status', header: 'Стан', width: 200, sortValue: (r) => r.status,
      cell: (r) => (
        <span title={r.error ?? undefined}>
          <DocStatusBadge label={STATUS_LABELS[r.status]} tone={STATUS_TONE[r.status]} />
          {r.status === 'RECOGNIZED' && r.checks_count > 0 && (
            <span style={{ marginLeft: 6, color: '#b45309', fontWeight: 700 }} title="Є зауваження перевірки">⚠ {r.checks_count}</span>
          )}
        </span>
      ) },
    { key: 'charged_credits', header: 'Кредити', width: 90, align: 'right',
      cell: (r) => (r.charged_credits ? fmt2(r.charged_credits) : ''), sortValue: (r) => Number(r.charged_credits ?? 0) },
    { key: 'uploaded_by_name', header: 'Завантажив', width: 160, cell: (r) => r.uploaded_by_name ?? '' },
    { key: 'created_at', header: 'Коли', width: 140, sortValue: (r) => r.created_at,
      cell: (r) => new Date(r.created_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) },
    { key: '_act', header: '', width: 90, cell: (r) => (
      r.status !== 'CONFIRMED' && canUpload ? (
        <button type="button" className="sup__shot" title="Прибрати з полки (файл видаляється)"
          onClick={async (e) => {
            e.stopPropagation();
            if (await confirm({ message: `Видалити «${r.filename}» з полки?`, danger: true, okLabel: 'видалити' })) remove.mutate(r.id);
          }}>видалити</button>
      ) : null
    ) },
  ], [canUpload, confirm, remove]);

  if (!loading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Оператор первинки</h2>
        <p style={muted}>Немає права переглядати вхідні документи в жодній організації.</p>
      </div>
    );
  }
  const rows = list.data?.items ?? [];
  const available = aiAvailableText(balance.data);

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Оператор первинки<AiBadge /></h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ minWidth: 220 }}>
          {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <button type="button" className={filter === 'open' ? 'primary' : undefined} onClick={() => setFilter('open')}>у роботі</button>
          <button type="button" className={filter === 'all' ? 'primary' : undefined} onClick={() => setFilter('all')}>усі</button>
        </div>
        <span style={{ ...muted, marginLeft: 'auto' }}>
          Oblik+/AI{available !== null ? ` · кредитів доступно: ${available}` : ''}
        </span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-lg)', padding: '1rem', margin: '0.5rem 0 1rem',
          background: dragOver ? 'var(--bg-soft)' : 'transparent', textAlign: 'center',
        }}>
        <div style={{ marginBottom: '0.5rem' }}>
          Перетягніть сюди рахунок, видаткову накладну або акт від постачальника —
          або{' '}
          <button type="button" className="primary" disabled={!canUpload || upload.isPending}
            title={canUpload ? undefined : 'Немає права завантажувати у цій організації'}
            onClick={() => fileRef.current?.click()}>
            {upload.isPending ? 'завантаження…' : 'обрати файли'}
          </button>
          <input ref={fileRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }}
            aria-label="Файли на розпізнавання"
            onChange={(e) => { pickFiles(e.target.files); e.target.value = ''; }} />
        </div>
        <div style={muted}>
          PDF, фото (JPEG/PNG/HEIC), Excel (.xlsx), Word (.docx); до 10 МБ і 20 сторінок кожен, до 20 файлів за раз.
          Розпізнавання займає від кількох секунд до хвилини; результат перевіряє бухгалтер — автоматично
          нічого не проводиться.
        </div>
      </div>

      <DataTable<IncomingDocumentListItem>
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText={filter === 'open' ? 'У роботі нічого немає — завантажте файл.' : 'Полка порожня.'}
        onRowDoubleClick={(r) => navigate(`/incoming-documents/${r.id}`)}
        rowMuted={(r) => (r.status === 'REJECTED' || r.status === 'SPLIT' ? 0.55 : undefined)}
        initialSortKey="created_at"
        initialSortDir="desc"
      />
      <p style={{ ...muted, marginBottom: 0 }}>Подвійний клік по рядку — відкрити перевірку.</p>
    </div>
  );
}
